import {
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    signal,
    SimpleChanges,
    viewChildren
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { interval, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { SupportLinkContent } from 'src/app/content/content.models';
import {
    AppService,
    AppSettingsDto,
    ETLRunStatus,
    ImpersonationUserDetailsDto,
    ListOrganisationDto,
    ListOrganisationUserDto,
    NotificationCategory,
    OrganisationService,
    ProductService,
    SchedulerConfig,
    UserAppFeaturesDto,
    UserRole,
    UserService,
    UserUIFeaturesDto,
    VolatileAppSettingsDto
} from 'src/app/services/api.services';
import { AppInitResult, AppInitService } from 'src/app/services/app-init.service';
import { SystemInfo } from 'src/app/services/models/support.models';
import { SupportService } from 'src/app/services/support.service';
import { EtlStatusBanner } from 'src/app/shared/components/layout/footer/footer-etl-status-banner/footer-etl-status-banner.component';
import { AppUser } from 'src/app/shared/models/app-user';
import { catchAppError, createSearchPattern, isNullOrWhitespace, Products } from 'src/app/shared/utils';
import { AppSelectors } from 'src/app/state/app';
import { SystemDialog } from 'src/app/support/dialogs/system/system.dialog';
import { clientConfig } from '../../../client-config';
import { VolatileSettingsDataService } from '../../services/data/volatile-settings-data.service';
import { ManageProfileData, ManageProfileDialog } from '../../shared/dialogs/manage-profile/manage-profile.dialog';
import { EtlBannerDisplaySettings } from '../../shared/models/etl-banner-display-settings';
import { SpendETLStatus } from '../../shared/models/etl-status';
import { DataHealthDialog } from '../../support/dialogs/data-health/data-health.dialog';
import { environment } from '../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppContext } from '../../app/context';
import { setLogCategoryActiveInStorage } from '../../pbir/utils/logger';

@Component({
  selector: 'app-layout-default',
  templateUrl: './default.component.html',
  styleUrls: ['./default.component.scss'],
  standalone: false,
})
export class LayoutDefaultComponent implements OnChanges, OnDestroy {
  readonly zone = inject(NgZone);
  readonly etlStatusBanner = inject(EtlStatusBanner);
  readonly destroyRef = inject(DestroyRef);
  readonly appContext = inject(AppContext);

  @Input() layoutParams: any;
  @Input() user: AppUser;

  @Output() logout = new EventEmitter();

  @Input() insightsLibraryVisible = false;
  @Output() openInsightsLibrary = new EventEmitter();

  @Input() supportLinks: SupportLinkContent[] = [];

  @Input() schedulerConfig: SchedulerConfig = null;

  @Input() features: UserAppFeaturesDto = null;

  readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  readonly sideBarItems = viewChildren('sidebarItem', { read: ElementRef });

  readonly sideBarItemsResponsiveQuery = computed(() => {
    let minHeight1 = ((this.sideBarItems()?.length ?? 0) * 60);
    let minHeight2 = ((this.sideBarItems()?.length ?? 0) * 45);
    let minHeight3 = ((this.sideBarItems()?.length ?? 0) * 38);
    let minScreenHeight1 = minHeight1 + 64;
    let minScreenHeight2 = minHeight2 + 64;
    let minScreenHeight3 = minHeight3 + 64;

    return `
      @media (max-height: ${minScreenHeight1}px) {
        .sidebar-item__text {
          font-size: 10px !important;
        }

        .sidebar-item, .sidebar-item__button {
          height: 45px !important;
        }

        .sidebar-item__icon {
          font-size: 14px !important;
          margin-bottom: 0 !important;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content:after {
          bottom: 9px;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content.sidebar-item-toggle__notification:after {
          bottom: 77px;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content.sidebar-item-toggle__support:after {
          bottom: 30px;
        }
      }

      @media (max-height: ${minScreenHeight2}px) {
        .sidebar-item__text {
          font-size: 8px !important;
        }

        .sidebar-item, .sidebar-item__button {
          height: 38px !important;
        }

        .sidebar-item__icon {
          font-size: 12px !important;
          margin-bottom: 0 !important;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content:after {
          bottom: 4px;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content.sidebar-item-toggle__notification:after {
          bottom: 60px;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content.sidebar-item-toggle__support:after {
          bottom: 19px;
        }
      }

      @media (max-height: ${minScreenHeight3}px) {
        .sidebar-item__text {
          font-size: 8px !important;
        }

        .sidebar-item, .sidebar-item__button {
          height: 30px !important;
        }

        .sidebar-item__icon {
          font-size: 11px !important;
          margin-bottom: 0 !important;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content:after {
          bottom: 0px;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content.sidebar-item-toggle__notification:after {
          bottom: 39px;
        }

        .mat-mdc-menu-panel.sidebar-item-toggle__content.sidebar-item-toggle__support:after {
          bottom: 9px;
        }
      }
    `;
  });

  private styleElement: HTMLStyleElement;

  readonly injectSideBarItemResponsiveQuery = effect(cleanup => {
    let [el, q] = [this.hostElement, this.sideBarItemsResponsiveQuery()];
    if (el?.nativeElement) {
      if (!this.styleElement) {
        this.styleElement = document.createElement('style');
        document.body.append(this.styleElement);
      }

      this.styleElement.innerHTML = q;
    }
  });

  businessUser = true;
  adminOrSupport = false;

  schedulerEnabled = false;

  changePasswordUrl = '';

  profileMenuOpened = false;
  supportMenuOpened = false;

  volatileAppSettings: VolatileAppSettingsDto;


  organisationId = null;
  organisations: (ListOrganisationDto & { hidden?: boolean })[] = [];
  activeOrganisationIsDifferent = false;
  activeOrganisationName = '';

  userName = '';
  email = '';

  productDisplayList: SendToProduct[];
  productURLsLoaded = false;

  loadingImpersonationableUsers = false;
  impersonationableUsersLoaded = false;
  impersonationableUsers: (ListOrganisationUserDto & { hidden?: boolean })[] = [];
  isImpersonating = false;
  impersonator: ImpersonationUserDetailsDto = null;
  impersonatedUser: ImpersonationUserDetailsDto = null;
  impersonatedUserId = 0;

  organisationFilterCtrl = new FormControl('');
  impersonationableUsersFilterCtrl = new FormControl('');

  showSessionInfo = signal(false);
  sessionExpiresOn = signal('');
  sessionExpiresIn = signal('');
  expiryPromptIn = signal('');
  sessionCheckSub = null;
  lastSessionCheckTime = signal(0);
  hideSessionInfo = signal(this.getHideSessionInfoUserPref());
  idleIn = signal('');

  readonly etlNotificationSettings = this.etlStatusBanner.settings;

  showDataHealth = false;

  settings: AppSettingsDto;
  notificationPanelDisabled: boolean;

  notificationMenuOpened = false;

  private getHideSessionInfoUserPref(): boolean {
    try {
      return JSON.parse(localStorage.getItem('hideSessionInfo'));
    }
    catch {
      return false;
    }
  }

  private setHideSessionInfoUserPref() {
    localStorage.setItem('hideSessionInfo', JSON.stringify(this.hideSessionInfo()));
  }

  toggleHideSessionInfo() {
    this.hideSessionInfo.set(!this.hideSessionInfo());
    this.setHideSessionInfoUserPref();
  }

  addToSessionExpiry(offset: number) {
    const date = this.authService.sessionExpiresAt;
    date.setTime(date.getTime() + offset);
    this.authService.sessionExpiresAt = date;
  }

  toggleDeidle() {
    this.authService.pauseDeidle = !this.authService.pauseDeidle;
  }

  toggleIdle() {
    const now = new Date();
    let time = this.authService.lastActiveAt;

    if (this.authService.isIdle)
      time = now;
    else
      time.setTime(now.getTime() - 30 * 1000);

    this.authService.lastActiveAt = time;
  }

  constructor(
    private store: Store,
    private dialog: MatDialog,
    public authService: AuthService,
    private appService: AppService,
    private supportService: SupportService,
    private organisationService: OrganisationService,
    private cookieService: CookieService,
    private productService: ProductService,
    private changeDetectionRef: ChangeDetectorRef,
    private userService: UserService,
    private appInitService: AppInitService,
    private volatileSettingsDataService: VolatileSettingsDataService) { }

  ngOnInit() {
    this.appInitService.done$.subscribe(() => {
      if (this.appInitService.result === AppInitResult.Unauthenticated)
        return;

      this.organisationFilterCtrl.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(f => {
          const filter = f?.trim().length ? createSearchPattern(f) : null;

          this.organisations.forEach(o => {
            o.hidden = !(!filter || filter.test(o.name) || (o.longName && filter.test(o.longName)));
          });

          this.changeDetectionRef.markForCheck();
        })
      ).subscribe();

      this.impersonationableUsersFilterCtrl.valueChanges.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(f => {
          const filter = f?.trim().length ? createSearchPattern(f) : null;

          this.impersonationableUsers.forEach(u => {
            u.hidden = !(!filter || filter.test(u.username));
          });

          this.changeDetectionRef.markForCheck();
        })
      ).subscribe();

      this.volatileSettingsDataService.volatileSettings$.subscribe(data => {
        if (data !== null) {
          const userRole = this.user?.role as UserRole;
          const uiFeatures = this.features?.ui;
          if (userRole && uiFeatures) {
            this.shouldDisplayDataHealth(data, userRole, uiFeatures);
            this.processVolatileSettings(data);
            this.changeDetectionRef.detectChanges();
          }
        }
      });

      this.store.select(AppSelectors.getSettings).subscribe((settings) => {
        this.applySettings(settings);
      });

      this.volatileSettingsDataService.bannerDismissed$.subscribe(res => {
        if (res) {
          this.etlStatusBanner.updateSettings(x => ({
            ...x,
            bannerDismissed: true
          }));
        }
      });

      this.volatileSettingsDataService.dataHealthModalStatus$.subscribe(data => {
        if (data) {
          this.dialog.open(DataHealthDialog, {
            width: '880px',
            height: '568px',
            data: { healthData$: this.volatileSettingsDataService.healthData$ }
          });
        }
      });

      if (!isCanvasRendererHost) {
        this.organisationService.list().pipe(
          catchAppError(err => {
            return of([]);
          }, { showErrorSnack: false }),

          tap(organisations => {
            this.organisations = organisations;
            this.organisations.sort(
              (a, b) => (a.longName ?? a.name).toLowerCase().localeCompare((b.longName ?? b.name).toLowerCase()));
          })
        ).subscribe();
      }
    });
  }

  ngOnDestroy() {
    this.styleElement?.remove();
    this.sessionCheckSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.features) {
      if (this.features?.general?.userImpersonation) {
        if (!this.impersonationableUsersLoaded) {
          this.getImpersonationTargetUsers();
        }
      }

      if (this.features?.general?.showSessionExpiryPanel) {
        this.zone.runOutsideAngular(() => {
          if (!this.sessionCheckSub) {
            this.showSessionInfo.set(true);

            this.zone.runOutsideAngular(() => {
              this.sessionCheckSub = setInterval(() => {
                this.zone.runOutsideAngular(() => {
                  if (!this.authService.loggedIn)
                    return;

                  const secondPassed = (window.performance.now() - this.lastSessionCheckTime()) >= 990;
                  if (secondPassed) {
                    const expiration = moment(this.authService.sessionExpiresAt);

                    const idleFor = (Date.now() - (this.authService.lastActiveAt?.getTime() ?? Date.now())) / 1000;
                    const idleIn = Math.max(30 - idleFor, 0);

                    this.sessionExpiresOn.set(expiration.format('LTS'));
                    this.sessionExpiresIn.set(humanizeTimeTo(expiration));
                    this.expiryPromptIn.set(
                      humanizeTimeTo(expiration.clone().subtract(
                        this.authService.sessionConfig.showSessionExpiryDialogPeriodMs, 'ms')));

                    this.idleIn.set(Math.ceil(idleIn).toFixed(0) + 's');
                    this.lastSessionCheckTime.set(window.performance.now());

                    function humanizeTimeTo(m: moment.Moment) {
                      return moment().to(m);
                    }
                  }
                });
              }, 150);
            });
          }
          else {
            this.showSessionInfo.set(false);
            clearInterval(this.sessionCheckSub);
          }
        });
      }
    }

    if (changes.user) {
      this.businessUser = (this.user?.role ?? -1) <= UserRole.ClientUser;
      this.adminOrSupport = this.user?.roles?.isAdmin || this.user?.roles?.isSupport;

      this.organisationId = this.user?.organisationId;

      this.setUsernameAndEmail(
        this.user?.impersonation?.impersonator?.name ?? this.user?.username,
        this.user?.claims);

      if (!this.businessUser)
        this.getProductUrls();

      this.activeOrganisationIsDifferent = this.user?.ownerOrganisationId != this.user?.organisationId;
      this.activeOrganisationName = this.user?.organisationDisplayName;

      this.isImpersonating = !!this.user?.impersonation?.target;

      if (this.isImpersonating) {
        this.impersonator = this.user.impersonation.impersonator;
        this.impersonatedUser = this.user.impersonation.target;
        this.impersonatedUserId = this.impersonatedUser?.id ?? 0;
      }
      else {
        this.impersonator = null;
        this.impersonatedUser = null;
        this.impersonatedUserId = 0;
      }
    }

    if (changes.schedulerConfig) {
      this.schedulerEnabled = !!this.schedulerConfig?.scheduler;
    }

    if ((!this.changePasswordUrl || (changes.user && this.user)) && clientConfig) {
      let query = `returnUrl=${encodeURIComponent(window.location.href)}`;

      if (this.user)
        query += `&username=${encodeURIComponent(this.user.username)}`;

      if (clientConfig.changePasswordUrl.indexOf('?') === -1)
        query = `?${query}`;

      this.changePasswordUrl = `${clientConfig.changePasswordUrl}${query}`;
    }
  }

  private processVolatileSettings(volatileSettings: VolatileAppSettingsDto): void {
    this.volatileAppSettings = volatileSettings;
    if (!volatileSettings.hasLastRun) {
      return;
    }

    if (this.etlNotificationSettings().etlRunId !== volatileSettings.spendETLStatus.etlRunId) {
      this.etlStatusBanner.clearSettings();
    }

    let title = '';
    let message = '';
    if (volatileSettings.spendETLStatus.etlRunStatus !== ETLRunStatus.Success) {
      if (this.etlNotificationSettings().etlStatus !== volatileSettings.spendETLStatus.etlRunStatus) {
        switch (volatileSettings.spendETLStatus.etlRunStatus) {
          case ETLRunStatus.Running:
            title = '<strong>Core Dataset Generation</strong>';
            message = ' - In Progress';
            break;
          case ETLRunStatus.Error:
            title = '<strong>Core Dataset Generation Failure</strong> ';
            message = ' - We are aware of this issue and working on resolving it.';
            break;
        }

        this.etlStatusBanner.setSettings({
          spendETLEnabled: volatileSettings.hasLastRun,
          etlRunId: volatileSettings.spendETLStatus.etlRunId,
          etlStatus: volatileSettings.spendETLStatus.etlRunStatus,
          bannerDismissed: false,
          title,
          message,
          organisationId: this.organisationId,
          showDataHealth: this.showDataHealth
        });
      }
    } else {
      this.etlStatusBanner.setSettings({
        spendETLEnabled: volatileSettings.hasLastRun,
        etlRunId: volatileSettings.spendETLStatus.etlRunId,
        etlStatus: SpendETLStatus.Success,
        bannerDismissed: false,
        title,
        message,
        organisationId: this.organisationId,
        showDataHealth: this.showDataHealth
      });
    }
  }

  shouldDisplayDataHealth(settings: VolatileAppSettingsDto, userRole: UserRole, uiFeatures: UserUIFeaturesDto): void {
    if(!settings.hasLastRun){
      this.showDataHealth = false;
      this.etlStatusBanner.clearSettings();
      return;
    }

    switch (userRole) {
      case UserRole.ClientUser as UserRole:
        this.showDataHealth = uiFeatures.dataHealthBusinessUser;
        break;
      case UserRole.InsightsUser as UserRole:
        this.showDataHealth = uiFeatures.dataHealthAdmin;
        break;
      case UserRole.ClientAdmin as UserRole:
        this.showDataHealth = uiFeatures.dataHealthSupportAdmin;
        break;
      case UserRole.InsightsAdmin as UserRole:
        this.showDataHealth = uiFeatures.dataHealthSuperAdmin;
        break;
      case UserRole.Developer as UserRole:
        this.showDataHealth = true;
        break;
      default:
        this.showDataHealth = false;
        break;
    }

    if (!this.showDataHealth) {
      this.etlStatusBanner.clearSettings();
    }
  }

  applySettings(settings: AppSettingsDto) {
    this.settings = settings;

      this.notificationPanelDisabled = true;

    if (settings.notificationSettings?.notificationsEnabled && settings.notificationSettings.sections[0].panelEnabled) {
      const sections = settings.notificationSettings.sections.filter(s=>s.category != NotificationCategory.All);
        for (const section of sections) {
          if (section.panelEnabled)
            this.notificationPanelDisabled = false;
        }
      }

      this.changeDetectionRef.markForCheck();
  }

  private setUsernameAndEmail(username: string, claims?: any) {
    if (!isNullOrWhitespace(claims?.given_name)) {
      this.userName = claims.given_name;

      if (!isNullOrWhitespace(claims?.family_name))
        this.userName = `${claims.given_name} ${claims.family_name}`;
    }
    else {
      this.userName = claims?.name;
    }

    if (isNullOrWhitespace(this.userName)) {
      if (username) {
        this.userName = username.split('@')[0];
      }
    }

    this.email = claims?.email ?? username;
  }

  manageProfile(tab = 0) {
    this.dialog.open<ManageProfileDialog, ManageProfileData>(ManageProfileDialog, {
      width: '985px',
      minHeight: '600px',
      data: {tab}
    });
  }

  showDialog(dialogName: string) {
    switch (dialogName) {
      case 'System':
        this.dialog.open(SystemDialog, {
          width: '720px',
          height: '400px',
        });
        break;
      case 'Data Health Status': {
        this.dialog.open(DataHealthDialog, {
          width: '880px',
          height: '568px',
          data: { healthData$: this.volatileSettingsDataService.healthData$ }
        });
        break;
      }
    }
  }

  getProductUrls() {
    this.productService.getProductURLs()
      .subscribe(result => {
        if (result.length <= 0)
          return;

        this.productDisplayList = result
          .map(
            product => <SendToProduct>{
              name: product.productName,
              logoUrl: Products[product.productName].logoURL,
              redirectUrl: product.productUrl
            });
        this.productURLsLoaded = true;

        this.changeDetectionRef.detectChanges();
      });
  }

  setSelectedOrganisation(organisationId: number | null) {
    this.etlStatusBanner.clearSettings();
    const accessToken = this.authService.accessToken;
    setTimeout(() => window.location.href = `/api/user/organisation?organisationId=${organisationId}&access_token=${accessToken}`, 200);
  }

  setImpersonatedUser(userId: number | null) {
    this.etlStatusBanner.clearSettings();
    const accessToken = this.authService.accessToken;
    setTimeout(() => window.location.href = `/api/user/impersonate?userId=${userId}&access_token=${accessToken}`, 200);
  }

  getImpersonationTargetUsers() {
    this.loadingImpersonationableUsers = true;
    this.impersonationableUsers = [];
    this.impersonationableUsersLoaded = true;

    this.userService.getImpersonationTargetUsers().pipe(
      tap(users => {
        users.sort((a, b) => a.username.localeCompare(b.username));
        this.impersonationableUsers = users;
      }),
      catchAppError(),
      finalize(() => this.loadingImpersonationableUsers = false)
    ).subscribe();
  }


  readonly showLoggerConfig = computed(() => environment.devLogLevels && this.appContext.user()?.role >= UserRole.InsightsAdmin);
  readonly loggerConfigToggle = signal(false);

  readonly logLevels = signal<LogLevel[]>([
    new LogLevel(this, 'debug'),
    new LogLevel(this, 'info'),
    new LogLevel(this, 'warn'),
    new LogLevel(this, 'error')
  ]);

  readonly logCategories = signal<LogCategory[]>([]);

  private logCategoryMap = new Map<string, LogCategory>();

  readonly logCategoriesUpdate = interval(500).pipe(
    takeUntilDestroyed(this.destroyRef),
    tap(() => {
      if (typeof (window as any)._insightsLogging.categories != 'object') {
        (window as any)._insightsLogging.categories = {};
        return;
      }

      let newCategories = [];

      for (let k of Object.keys((window as any)._insightsLogging.categories)) {
        let category = this.logCategoryMap.get(k.toLowerCase());
        if (!category) {
          category = new LogCategory(this, k);
          newCategories.push(category);
          this.logCategoryMap.set(k.toLowerCase(), category);
        }

        category.enabled.set(!!(window as any)._insightsLogging.categories[k]);
      }

      if (newCategories.length)
        this.logCategories.update(x => [...x, ...newCategories]);
    })
  ).subscribe();
}

class LogLevel {
  constructor(
    readonly component: LayoutDefaultComponent,
    readonly name: string) { }

  readonly enabled = signal(true);

  setEnabled(value: boolean) {
    this.enabled.set(
      (window as any)._insightsLogging[this.name] = value
    );
  }
}

class LogCategory {
  constructor(
    readonly component: LayoutDefaultComponent,
    readonly name: string) { }

  readonly enabled = signal(true);

  setEnabled(value: boolean) {
    if (typeof (window as any)._insightsLogging.categories != 'object')
      (window as any)._insightsLogging.categories = {};

    this.enabled.set(
      (window as any)._insightsLogging.categories[this.name] = value
    );

    setLogCategoryActiveInStorage(this.name, value);
  }
}


interface SendToProduct {
  name: string
  logoUrl: string
  redirectUrl: string
}
